import { useState } from 'react';
import { mensagemDoErro, useDocArticle, useDocs } from '@/lib/useDocs';
import { useAuth } from '@/nucleo/auth/useAuth';
import { DocsSidebar } from './DocsSidebar';
import { DocsViewer } from './DocsViewer';
import { DocsEditor } from './DocsEditor';
import { DocsVersionHistory } from './DocsVersionHistory';
import { NewArticleDialog } from './NewArticleDialog';
import { PageHeader } from '@/ui/PageHeader';
import { Spinner } from '@/ui/Spinner';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { BookOpen, PanelLeftClose, PanelLeft } from 'lucide-react';
import { Button } from '@/ui/base/button';
import { toast } from 'sonner';
import { cn } from '@/nucleo/utilitarios';
import { useIsMobile } from '@/nucleo/ganchos/use-mobile';

export default function AdminDocs() {
  const {
    articles,
    loading,
    error,
    refetch,
    updateArticle,
    restoreVersion,
    createArticle,
    deleteArticle,
  } = useDocs();
  const { temPapel } = useAuth();
  const isAdmin = temPapel('ADMIN') || temPapel('GESTOR_ADMIN');
  const isMobile = useIsMobile();

  const [activeSlug, setActiveSlug] = useState<string | null>(null);
  const [editing, setEditing] = useState(false);
  // A versão que a pessoa viu ao abrir o editor: é ela que o salvamento informa,
  // para o banco recusar se outra pessoa alterou o artigo no meio do caminho.
  const [versaoAoEditar, setVersaoAoEditar] = useState<number | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showNewArticle, setShowNewArticle] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);

  const activeSummary = articles.find((a) => a.slug === activeSlug) ?? null;
  const detail = useDocArticle(activeSummary?.id ?? null);
  const activeArticle = detail.article;
  const temSubartigos = activeSummary
    ? articles.some((a) => a.parent_id === activeSummary.id)
    : false;

  // Auto-select first article
  if (!activeSlug && articles[0] && !loading) {
    setActiveSlug(articles[0].slug);
  }

  const handleEdit = () => {
    if (!activeArticle) return;
    setVersaoAoEditar(activeArticle.versao);
    setEditing(true);
  };

  const handleSave = async (markdown: string) => {
    if (!activeArticle) return;
    try {
      await updateArticle.mutateAsync({
        id: activeArticle.id,
        versao: versaoAoEditar ?? activeArticle.versao,
        conteudo_markdown: markdown,
      });
      toast.success('Artigo salvo com sucesso');
      setEditing(false);
    } catch (erro) {
      toast.error(mensagemDoErro(erro, 'Não foi possível salvar o artigo. Tente de novo.'));
    }
  };

  const handleRestore = async (numero: number) => {
    if (!activeArticle) return;
    try {
      await restoreVersion.mutateAsync({
        id: activeArticle.id,
        versao: activeArticle.versao,
        numero,
      });
      toast.success('Versão restaurada com sucesso');
      setShowHistory(false);
      setEditing(false);
    } catch (erro) {
      toast.error(mensagemDoErro(erro, 'Não foi possível restaurar a versão. Tente de novo.'));
    }
  };

  const handleCreateArticle = async (data: Parameters<typeof createArticle.mutateAsync>[0]) => {
    try {
      const criado = await createArticle.mutateAsync(data);
      toast.success('Artigo criado com sucesso');
      setShowNewArticle(false);
      setActiveSlug(data.slug);
      setVersaoAoEditar(criado.versao);
      setEditing(true);
    } catch (erro) {
      toast.error(mensagemDoErro(erro, 'Não foi possível criar o artigo. Tente de novo.'));
    }
  };

  const handleDeleteArticle = async () => {
    if (!activeArticle) return;
    try {
      await deleteArticle.mutateAsync({ id: activeArticle.id, versao: activeArticle.versao });
      toast.success('Artigo excluído com sucesso');
      setActiveSlug(null);
      setEditing(false);
    } catch (erro) {
      toast.error(mensagemDoErro(erro, 'Não foi possível excluir o artigo. Tente de novo.'));
    }
  };

  if (loading)
    return <Spinner size="lg" className="min-h-[50vh]" rotulo="Carregando a documentação" />;

  if (error) {
    return (
      <div className="px-4 sm:px-6 py-3">
        <PageHeader title="Documentação Técnica" />
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar a documentação"
          description="O sistema não conseguiu buscar os artigos agora. Tente de novo em instantes."
          primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <div className="px-4 sm:px-6 py-3 border-b border-border flex items-center gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label={sidebarOpen ? 'Fechar menu lateral' : 'Abrir menu lateral'}
        >
          {sidebarOpen ? <PanelLeftClose className="h-4 w-4" /> : <PanelLeft className="h-4 w-4" />}
        </Button>
        <PageHeader title="Documentação Técnica" />
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div
          className={cn(
            'transition-all duration-200 overflow-hidden shrink-0',
            sidebarOpen ? 'w-72' : 'w-0',
          )}
        >
          {sidebarOpen && (
            <DocsSidebar
              articles={articles}
              activeSlug={activeSlug}
              onSelect={(slug) => {
                setActiveSlug(slug);
                setEditing(false);
                if (isMobile) setSidebarOpen(false);
              }}
              isAdmin={isAdmin}
              onNewArticle={() => setShowNewArticle(true)}
            />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {articles.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={BookOpen}
                title="Nenhum artigo ainda"
                description={
                  isAdmin
                    ? 'A documentação ainda não tem artigos. Crie o primeiro para começar.'
                    : 'A documentação ainda não tem artigos.'
                }
                actionLabel={isAdmin ? 'Novo artigo' : undefined}
                onAction={isAdmin ? () => setShowNewArticle(true) : undefined}
              />
            </div>
          ) : !activeSummary ? (
            <div className="flex-1 flex items-center justify-center">
              <EmptyState
                icon={BookOpen}
                title="Documentação Técnica"
                description="Selecione um artigo na barra lateral para visualizar."
              />
            </div>
          ) : detail.loading ? (
            <Spinner size="lg" className="min-h-[50vh]" rotulo="Carregando o artigo" />
          ) : detail.error || !activeArticle ? (
            <div className="flex-1 flex items-center justify-center">
              <ErrorState
                variant="load-failed"
                title="Não foi possível abrir o artigo"
                description="O artigo pode ter sido excluído por outra pessoa. Tente de novo ou escolha outro na barra lateral."
                primaryAction={{ label: 'Tentar de novo', onClick: () => void detail.refetch() }}
              />
            </div>
          ) : editing ? (
            <DocsEditor
              article={activeArticle}
              onSave={handleSave}
              onCancel={() => setEditing(false)}
              saving={updateArticle.isPending}
            />
          ) : (
            <DocsViewer
              article={activeArticle}
              isAdmin={isAdmin}
              onEdit={handleEdit}
              onShowHistory={() => setShowHistory(true)}
              // Artigo com subartigos não se exclui (o banco recusa): o botão nem aparece.
              onDelete={temSubartigos ? undefined : handleDeleteArticle}
            />
          )}
        </div>
      </div>

      {/* Version History Sheet */}
      <DocsVersionHistory
        articleId={activeArticle?.id ?? null}
        open={showHistory}
        onOpenChange={setShowHistory}
        onRestore={handleRestore}
        restoring={restoreVersion.isPending}
      />

      <NewArticleDialog
        open={showNewArticle}
        onOpenChange={setShowNewArticle}
        articles={articles}
        onSubmit={handleCreateArticle}
        saving={createArticle.isPending}
      />
    </div>
  );
}
